'use client';

import { useEffect } from 'react';
import { trackEvent } from '@/lib/analytics/contract';

/**
 * Core Web Vitals monitoring component for SEO performance tracking
 * Tracks LCP, INP, CLS, FCP, and TTFB metrics (web-vitals v5 API)
 */
export function CoreWebVitals() {
  useEffect(() => {
    // Only run in production and if Web Vitals API is available
    if (process.env.NODE_ENV !== 'production' || typeof window === 'undefined') {
      return;
    }

    // Dynamic import to avoid SSR issues - web-vitals v5 uses onXXX naming
    import('web-vitals')
      .then(({ onCLS, onINP, onFCP, onLCP, onTTFB }) => {
        // Track Core Web Vitals
        onCLS((metric) => {
          sendToAnalytics('CLS', metric.value, metric.rating);
        });

        // INP replaced FID in web-vitals v4+
        onINP((metric) => {
          sendToAnalytics('INP', metric.value, metric.rating);
        });

        onFCP((metric) => {
          sendToAnalytics('FCP', metric.value, metric.rating);
        });

        onLCP((metric) => {
          sendToAnalytics('LCP', metric.value, metric.rating);
        });

        onTTFB((metric) => {
          sendToAnalytics('TTFB', metric.value, metric.rating);
        });
      })
      .catch((error) => {
        console.warn('Web Vitals tracking failed:', error);
      });
  }, []);

  return null; // This component doesn't render anything
}

/**
 * Send Core Web Vitals data to analytics
 */
function sendToAnalytics(
  name: 'CLS' | 'INP' | 'FCP' | 'LCP' | 'TTFB',
  value: number,
  rating: 'good' | 'needs-improvement' | 'poor',
) {
  trackEvent('web_vital', {
    metric: name,
    rating,
    value: Math.round(name === 'CLS' ? value * 1000 : value),
  });

  // Also log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Core Web Vitals] ${name}:`, value, `(${rating})`);
  }
}
